import { useEffect, useRef, useState } from 'react';

// Pointer-based drag-to-reorder that works for both mouse (drag from a grip
// handle) and touch (long-press then drag — either the handle or, more
// forgivingly, anywhere on the row that isn't itself a control). A single
// instance handles every reorderable list on the page — each caller passes a
// `scopeId` (e.g. a list's id, or a sentinel for the top-level lists
// collection) so only one row-set is ever "live" at a time, which sidesteps
// calling hooks inside a .map().

type Axis = 'y' | 'grid';

interface DragState {
  scopeId: string;
  id: string;
  order: string[];
  axis: Axis;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  /** Where within the box the pointer grabbed it, 0..1, so the box doesn't recenter under the pointer. */
  grabX: number;
  grabY: number;
}

interface Pending {
  timer: ReturnType<typeof setTimeout>;
  cleanup: () => void;
}

const TOUCH_HOLD_MS = 220;
const MOVE_CANCEL_PX = 10;
// Form controls are always exempt; other controls (checkbox, delete, etc.)
// opt out individually via data-no-drag so the title/name button — which
// covers most of a row's tappable area — can still be long-pressed to drag.
const NOT_DRAGGABLE_SELECTOR = 'input, textarea, select, [data-no-drag]';

export interface DragPreview {
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  grabX: number;
  grabY: number;
}

export interface DragReorder {
  /** Current display order for a scope: the live preview while dragging, else `ids` unchanged. */
  getOrder(scopeId: string, ids: string[]): string[];
  /** True while this exact row is the one being dragged. */
  isDragging(scopeId: string, id: string): boolean;
  /** Ref callback to attach to each row's outer element, for hit-testing during drag. */
  registerRow(scopeId: string, id: string): (el: HTMLElement | null) => void;
  /** A floating "you're grabbing this" badge that follows the pointer, or null when idle. */
  preview: DragPreview | null;
  /** Spread onto a row/card's outer element: long-press anywhere that isn't a button/input starts a touch drag. Ignored for mouse (use dragHandleProps there). */
  rowDragProps(scopeId: string, id: string, ids: string[], onCommit: (order: string[]) => void, axis: Axis, label: string): {
    onPointerDown: (e: React.PointerEvent) => void;
  };
  /** Spread onto a small grip handle: immediate drag on mouse, long-press on touch/pen. */
  dragHandleProps(scopeId: string, id: string, ids: string[], onCommit: (order: string[]) => void, axis: Axis, label: string): {
    onPointerDown: (e: React.PointerEvent) => void;
    className: string;
    'aria-label': string;
    title: string;
  };
}

export function useDragReorder(): DragReorder {
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const commitRef = useRef<((order: string[]) => void) | null>(null);
  const rowRefs = useRef(new Map<string, HTMLElement>());
  const pending = useRef<Pending | null>(null);
  const suppressClick = useRef(false);

  useEffect(() => { dragRef.current = drag; }, [drag]);

  // Swallow the synthetic click that follows a completed drag release, so a
  // long-press-drag on a title/row doesn't also trigger its tap action.
  useEffect(() => {
    const onClickCapture = (e: MouseEvent) => {
      if (suppressClick.current) {
        suppressClick.current = false;
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('click', onClickCapture, true);
    return () => window.removeEventListener('click', onClickCapture, true);
  }, []);

  useEffect(() => {
    if (!drag) return;
    const move = (e: PointerEvent) => {
      e.preventDefault();
      const state = dragRef.current;
      if (!state) return;
      let bestId: string | null = null;
      let bestDist = Infinity;
      let bestAfter = false;
      for (const rid of state.order) {
        if (rid === state.id) continue;
        const el = rowRefs.current.get(`${state.scopeId}::${rid}`);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        const dist = dx * dx + dy * dy;
        if (dist < bestDist) {
          bestDist = dist;
          bestId = rid;
          bestAfter = state.axis === 'y' ? dy > 0 : (dx / r.width + dy / r.height) > 0;
        }
      }
      let nextOrder = state.order;
      if (bestId) {
        const rest = state.order.filter((x) => x !== state.id);
        const idx = rest.indexOf(bestId);
        rest.splice(bestAfter ? idx + 1 : idx, 0, state.id);
        if (rest.join('|') !== state.order.join('|')) nextOrder = rest;
      }
      setDrag({ ...state, order: nextOrder, x: e.clientX, y: e.clientY });
    };
    const up = () => {
      const state = dragRef.current;
      setDrag(null);
      if (state) {
        suppressClick.current = true;
        setTimeout(() => { suppressClick.current = false; }, 300);
        commitRef.current?.(state.order);
      }
      commitRef.current = null;
    };
    const body = document.body.style;
    body.userSelect = 'none';
    (body as unknown as { webkitUserSelect: string }).webkitUserSelect = 'none';
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      body.userSelect = '';
      (body as unknown as { webkitUserSelect: string }).webkitUserSelect = '';
    };
    // Only (re)attach when a new drag session actually starts/ends — order
    // updates within the same session are read live via dragRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag?.scopeId, drag?.id]);

  function getOrder(scopeId: string, ids: string[]): string[] {
    return drag && drag.scopeId === scopeId ? drag.order : ids;
  }

  function isDragging(scopeId: string, id: string): boolean {
    return drag?.scopeId === scopeId && drag.id === id;
  }

  function registerRow(scopeId: string, id: string) {
    return (el: HTMLElement | null) => {
      const key = `${scopeId}::${id}`;
      if (el) rowRefs.current.set(key, el);
      else rowRefs.current.delete(key);
    };
  }

  function start(scopeId: string, id: string, ids: string[], onCommit: (order: string[]) => void, axis: Axis, label: string, x: number, y: number) {
    commitRef.current = onCommit;
    const el = rowRefs.current.get(`${scopeId}::${id}`);
    const r = el?.getBoundingClientRect();
    const width = r?.width ?? 200;
    const height = r?.height ?? 40;
    const grabX = r ? Math.min(Math.max((x - r.left) / width, 0), 1) : 0.5;
    const grabY = r ? Math.min(Math.max((y - r.top) / height, 0), 1) : 0.5;
    setDrag({ scopeId, id, order: ids, axis, label, x, y, width, height, grabX, grabY });
  }

  function cancelPending() {
    if (pending.current) {
      clearTimeout(pending.current.timer);
      pending.current.cleanup();
      pending.current = null;
    }
  }

  function armLongPress(scopeId: string, id: string, ids: string[], onCommit: (order: string[]) => void, axis: Axis, label: string, e: React.PointerEvent) {
    cancelPending();
    const x = e.clientX;
    const y = e.clientY;
    const onMove = (ev: PointerEvent) => {
      if (Math.abs(ev.clientX - x) > MOVE_CANCEL_PX || Math.abs(ev.clientY - y) > MOVE_CANCEL_PX) cancelPending();
    };
    const onUp = () => cancelPending();
    const timer = setTimeout(() => {
      cancelPending();
      start(scopeId, id, ids, onCommit, axis, label, x, y);
    }, TOUCH_HOLD_MS);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, { once: true });
    window.addEventListener('pointercancel', onUp, { once: true });
    pending.current = {
      timer,
      cleanup: () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      },
    };
  }

  function dragHandleProps(scopeId: string, id: string, ids: string[], onCommit: (order: string[]) => void, axis: Axis, label: string) {
    return {
      onPointerDown: (e: React.PointerEvent) => {
        e.stopPropagation(); // don't also arm an ancestor row/card's drag
        if (e.pointerType === 'mouse') {
          if (e.button !== 0) return;
          e.preventDefault();
          start(scopeId, id, ids, onCommit, axis, label, e.clientX, e.clientY);
          return;
        }
        armLongPress(scopeId, id, ids, onCommit, axis, label, e);
      },
      className: 'pl-drag-handle',
      'aria-label': 'Drag to reorder',
      title: 'Drag to reorder',
    };
  }

  function rowDragProps(scopeId: string, id: string, ids: string[], onCommit: (order: string[]) => void, axis: Axis, label: string) {
    return {
      onPointerDown: (e: React.PointerEvent) => {
        if (e.pointerType === 'mouse') return; // desktop users grab the handle
        if ((e.target as HTMLElement).closest(NOT_DRAGGABLE_SELECTOR)) return;
        e.stopPropagation(); // don't also arm an ancestor row/card's drag (e.g. an item inside a list card)
        armLongPress(scopeId, id, ids, onCommit, axis, label, e);
      },
    };
  }

  return {
    getOrder,
    isDragging,
    registerRow,
    preview: drag ? { label: drag.label, x: drag.x, y: drag.y, width: drag.width, height: drag.height, grabX: drag.grabX, grabY: drag.grabY } : null,
    rowDragProps,
    dragHandleProps,
  };
}
